import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Container
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Inventory,
  PlaylistAddCheck,
  Save,
} from '@mui/icons-material';
import { purchaseAPI, racksAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

export const ReceivePurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const isPartial = searchParams.get('partial') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [batches, setBatches] = useState([]);
  const [racks, setRacks] = useState([]); // New state for racks
  const [activeStep, setActiveStep] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const steps = ['Review Order', 'Enter Batch Details', 'Confirmation'];

  useEffect(() => {
    fetchPurchaseOrder();
    fetchRacks(); // Fetch racks on component mount
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      const response = await purchaseAPI.getById(id);
      const order = response.data.data.purchaseOrder;
      setPurchaseOrder(order);

      // Initialize batches for each item
      const initialBatches = order.items.map((item, index) => ({
        itemIndex: index,
        batchNumber: generateBatchNumber(item.medicine),
        quantity: isPartial ? 0 : Number(item.quantity),
        expiryDate: '',
        manufactureDate: new Date().toISOString().split('T')[0],
        rack: '', // Initialize with empty rack
        receivedQuantity: isPartial ? 0 : Number(item.quantity),
      }));

      setBatches(initialBatches);
    } catch (error) {
      enqueueSnackbar('Error fetching purchase order', { variant: 'error' });
      navigate('/hospital/pharmacy/purchases');
    } finally {
      setLoading(false);
    }
  };

  // Fetch racks from backend API
  const fetchRacks = async () => {
    try {
      // Fetch active racks with available capacity
      const response = await racksAPI.getAvailable();
      if (response.data.success) {
        setRacks(response.data.data.racks || []);
      } else {
        enqueueSnackbar('Error fetching racks', { variant: 'error' });
        // Fallback to empty array
        setRacks([]);
      }
    } catch (error) {
      console.error('Error fetching racks:', error);
      enqueueSnackbar('Error fetching rack locations', { variant: 'error' });
      // Fallback to empty array to prevent breaking the form
      setRacks([]);
    }
  };

  const generateBatchNumber = (medicine) => {
    const prefix = medicine.name.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  };

  const handleBatchChange = (index, field, value) => {
    const updatedBatches = [...batches];
    updatedBatches[index] = {
      ...updatedBatches[index],
      [field]: value
    };

    // If it's partial receiving and quantity changes, update receivedQuantity
    if (isPartial && field === 'quantity') {
      updatedBatches[index].receivedQuantity = parseInt(value) || 0;
    }

    if (field === 'quantity') {
      updatedBatches[index][field] = Number(value) || 0;
    }

    setBatches(updatedBatches);
  };

  const validateBatches = () => {
    for (const batch of batches) {
      if (!batch.batchNumber.trim()) {
        enqueueSnackbar('Batch number is required for all items', { variant: 'warning' });
        return false;
      }
      if (!batch.expiryDate) {
        enqueueSnackbar('Expiry date is required for all items', { variant: 'warning' });
        return false;
      }
      if (!batch.manufactureDate) {
        enqueueSnackbar('Manufacture date is required for all items', { variant: 'warning' });
        return false;
      }
      if (isPartial && (batch.quantity <= 0 || batch.quantity > purchaseOrder.items[batch.itemIndex].quantity)) {
        enqueueSnackbar(`Invalid quantity for ${purchaseOrder.items[batch.itemIndex].medicine.name}`, { variant: 'warning' });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 1 && !validateBatches()) {
      return;
    }
    if (activeStep === 2) {
      setConfirmationOpen(true);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReceive = async () => {
    if (!validateBatches()) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        batches: batches
          .filter(batch => {
            // For partial receiving, only include items with quantity > 0
            const quantity = isPartial ? batch.quantity : purchaseOrder.items[batch.itemIndex].quantity;
            return quantity > 0;
          })
          .map(batch => ({
            itemIndex: batch.itemIndex,
            batchNumber: batch.batchNumber,
            expiryDate: batch.expiryDate,
            manufactureDate: batch.manufactureDate,
            rack: batch.rack || undefined, // Send rack ID if selected, otherwise undefined (optional)
            quantity: isPartial ? Number(batch.quantity) : undefined
          }))
      };

      // Use purchaseAPI instead of direct api call
      await purchaseAPI.receive(id, payload);
      
      enqueueSnackbar(
        `Purchase order ${isPartial ? 'partially received' : 'received'} successfully`,
        { variant: 'success' }
      );
      
      setConfirmationOpen(false);
      navigate('/hospital/pharmacy/purchases');
    } catch (error) {
      console.error('Receive error:', error);
      const errorMessage = error.response?.data?.message || 'Error receiving purchase order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const calculateReceivedTotal = () => {
    return batches.reduce((total, batch) => {
      const item = purchaseOrder?.items[batch.itemIndex];
      if (item) {
        const quantity = isPartial ? batch.quantity : item.quantity;
        return total + (quantity * item.costPrice);
      }
      return total;
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h4">Loading...</Typography>
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h4">Purchase order not found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack sx={{ fontSize: 28 }} />}
            onClick={() => navigate('/hospital/pharmacy/purchases')}
            sx={{ fontSize: '1.1rem' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {isPartial ? 'Partially Receive' : 'Receive'} Purchase Order
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
              {purchaseOrder.poNumber} - {purchaseOrder.supplier.name}
            </Typography>
          </Box>
        </Box>
        
        <Chip
          icon={isPartial ? <PlaylistAddCheck sx={{ fontSize: 24 }} /> : <CheckCircle sx={{ fontSize: 24 }} />}
          label={isPartial ? 'Partial Receiving' : 'Complete Receiving'}
          color={isPartial ? 'warning' : 'success'}
          variant="outlined"
          sx={{ fontSize: '1.1rem', height: '36px' }}
        />
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ fontSize: '1.1rem' }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 1: Review Order */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Order Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Ordered Qty</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost Price</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              {item.medicine.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {item.medicine.genericName} • {item.medicine.strength}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                              {item.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                              ₹{item.costPrice}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                              ₹{(item.quantity * item.costPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Order Summary
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Supplier
                  </Typography>
                  <Typography variant="h6" fontWeight="medium" sx={{ fontSize: '1.2rem' }}>
                    {purchaseOrder.supplier.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    {purchaseOrder.supplier.contactPerson} • {purchaseOrder.supplier.phone}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Order Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                    {new Date(purchaseOrder.orderDate).toLocaleDateString()}
                  </Typography>
                </Box>

                {purchaseOrder.expectedDeliveryDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Expected Delivery
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                      {new Date(purchaseOrder.expectedDeliveryDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{purchaseOrder.totalAmount?.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Tax:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{purchaseOrder.taxAmount?.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>Total:</Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                      ₹{purchaseOrder.grandTotal?.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Alert 
                  severity={isPartial ? 'warning' : 'info'} 
                  sx={{ 
                    mb: 2,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                >
                  {isPartial 
                    ? 'You are about to partially receive this order. Enter quantities for items you are receiving.'
                    : 'You are about to mark this entire order as received.'
                  }
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNext}
                  startIcon={<Inventory sx={{ fontSize: 24 }} />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Continue to Batch Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Batch Details */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Enter Batch Details
                </Typography>
                
                {isPartial && (
                  <Alert severity="info" sx={{ 
                    mb: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}>
                    Enter the quantities you are receiving for each item. You can receive less than the ordered quantity.
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Ordered Qty</TableCell>
                        {isPartial && <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Receiving Qty</TableCell>}
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Manufacture Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Rack/Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {item.medicine.genericName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            {isPartial && (
                              <TableCell align="center" sx={{ py: 2 }}>
                                <TextField
                                  type="number"
                                  size="medium"
                                  value={batch.quantity}
                                  onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)}
                                  inputProps={{ 
                                    min: 0, 
                                    max: item.quantity,
                                    style: { 
                                      textAlign: 'center',
                                      fontSize: '1.1rem'
                                    }
                                  }}
                                  sx={{ width: 100 }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                value={batch.batchNumber}
                                onChange={(e) => handleBatchChange(index, 'batchNumber', e.target.value)}
                                placeholder="Batch number"
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                type="date"
                                size="medium"
                                value={batch.manufactureDate}
                                onChange={(e) => handleBatchChange(index, 'manufactureDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                type="date"
                                size="medium"
                                value={batch.expiryDate}
                                onChange={(e) => handleBatchChange(index, 'expiryDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <FormControl size="medium" sx={{ minWidth: 140 }}>
                                <InputLabel sx={{ fontSize: '1.1rem' }}>Select Rack</InputLabel>
                                <Select
                                  value={batch.rack || ''}
                                  label="Select Rack"
                                  onChange={(e) => handleBatchChange(index, 'rack', e.target.value)}
                                  sx={{ fontSize: '1.1rem' }}
                                >
                                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>
                                    <em>None</em>
                                  </MenuItem>
                                  {racks.map((rack) => (
                                    <MenuItem key={rack._id} value={rack._id} sx={{ fontSize: '1.1rem' }}>
                                      {rack.name} {rack.location ? `(${rack.location.aisle}-${rack.location.row}-${rack.location.section})` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {isPartial && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontSize: '1.2rem' }}>
                      Receiving Summary
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      Total Value: ₹{calculateReceivedTotal().toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button 
                    onClick={handleBack} 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleNext} 
                    variant="contained"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Review & Confirm
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3: Confirmation */}
      {activeStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Confirmation
                </Typography>
                
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                  icon={<CheckCircle sx={{ fontSize: 28 }} />}
                >
                  Ready to {isPartial ? 'partially receive' : 'receive'} this order. Please review the details below.
                </Alert>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Rack Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        const quantity = isPartial ? batch.quantity : item.quantity;
                        const selectedRack = racks.find(rack => rack._id === batch.rack);
                        
                        if (isPartial && quantity === 0) return null;
                        
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {quantity}
                                {isPartial && (
                                  <Typography variant="body1" color="text.secondary" display="block" sx={{ fontSize: '1rem' }}>
                                    of {item.quantity} ordered
                                  </Typography>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {batch.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {new Date(batch.expiryDate).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {selectedRack ? `${selectedRack.name} ${selectedRack.location ? `(${selectedRack.location})` : ''}` : 'Not assigned'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button 
                    onClick={handleBack} 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleNext} 
                    variant="contained"
                    startIcon={<CheckCircle sx={{ fontSize: 24 }} />}
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    {isPartial ? 'Partially Receive Order' : 'Receive Complete Order'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem' }}>
          Confirm {isPartial ? 'Partial' : 'Complete'} Receiving
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ 
            mb: 2,
            fontSize: '1.1rem',
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }}>
            This action will update inventory stock and cannot be undone.
          </Alert>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Are you sure you want to {isPartial ? 'partially receive' : 'mark as received'} purchase order{' '}
            <strong>{purchaseOrder.poNumber}</strong>?
          </Typography>
          {isPartial && (
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1, fontSize: '1.1rem' }}>
              Total value: ₹{calculateReceivedTotal().toFixed(2)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmationOpen(false)}
            sx={{ fontSize: '1.1rem' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReceive} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? null : <Save sx={{ fontSize: 24 }} />}
            sx={{ fontSize: '1.1rem' }}
          >
            {saving ? 'Processing...' : 'Confirm Receiving'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};